import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from "@exhousing/design-system";
import { Building2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  useCreateProperty,
  useProperties,
  usePropertyLookups,
} from "@/hooks/api/useProperties";
import { useLandlords } from "@/hooks/api/useParties";
import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";

function formatRent(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

export function PropertiesPage() {
  const [cityFilter, setCityFilter] = useState("");
  const [publishedOnly, setPublishedOnly] = useState(false);
  const { data: properties, isLoading } = useProperties({
    city: cityFilter || undefined,
    published: publishedOnly || undefined,
  });
  const { data: lookups } = usePropertyLookups();
  const { data: landlords } = useLandlords();
  const { data: me } = useMe();
  const landlordView = isLandlord(me);
  const createProperty = useCreateProperty();
  const [showForm, setShowForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open the intake form when arrived via the ⌘K "New property" quick-create.
  // Landlords never create — the agency owns the portfolio — so ignore the flag.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      if (!landlordView) setShowForm(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, landlordView]);

  const types = (lookups ?? []).filter((l) => l.category === "type");
  // Cross-org (super-admin) responses carry the owning agency name.
  const showAgency = (properties ?? []).some((p) => p.organization_name);

  const [form, setForm] = useState({
    street: "",
    house_number: "",
    city: "",
    advertised_rent: "",
    type_id: "",
    landlord_id: "",
  });

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.street.trim() || !form.city.trim()) {
      toast.error("Street and city are required so the listing is identifiable");
      return;
    }
    createProperty.mutate(
      {
        street: form.street || null,
        house_number: form.house_number || null,
        city: form.city || null,
        advertised_rent: form.advertised_rent ? Number(form.advertised_rent) : null,
        type_id: form.type_id || null,
        landlord_id: form.landlord_id || null,
        country: "NL",
        free_sector: true,
      },
      {
        onSuccess: () => {
          toast.success("Property created");
          setShowForm(false);
          setForm({
            street: "",
            house_number: "",
            city: "",
            advertised_rent: "",
            type_id: "",
            landlord_id: "",
          });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Properties</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {properties ? `${properties.length} in your portfolio` : "Loading…"}
          </p>
        </div>
        {!landlordView && (
          <Button onClick={() => setShowForm((s) => !s)}>
            <Plus className="size-4" />
            New property
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by city…"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={publishedOnly}
            onChange={(e) => setPublishedOnly(e.target.checked)}
            className="size-4 rounded border-input accent-accent"
          />
          Published only
        </label>
      </div>

      {showForm && !landlordView && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <div className="mb-4">
              <h2 className="font-display text-lg font-medium">New property</h2>
              <p className="text-sm text-muted-foreground">
                Street and city identify the listing — the rest can follow on the
                detail page.
              </p>
            </div>
            <form onSubmit={onCreate} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="street">Street *</Label>
                <Input
                  id="street"
                  required
                  value={form.street}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="house_number">House number</Label>
                <Input
                  id="house_number"
                  value={form.house_number}
                  onChange={(e) => setForm({ ...form, house_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  required
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rent">Advertised rent (€/mo)</Label>
                <Input
                  id="rent"
                  type="number"
                  value={form.advertised_rent}
                  onChange={(e) => setForm({ ...form, advertised_rent: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="type">Type</Label>
                <select
                  id="type"
                  value={form.type_id}
                  onChange={(e) => setForm({ ...form, type_id: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-card px-3.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select a type…</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label_en}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="landlord">Landlord</Label>
                <select
                  id="landlord"
                  value={form.landlord_id}
                  onChange={(e) => setForm({ ...form, landlord_id: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-card px-3.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">— No landlord —</option>
                  {(landlords ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.contact.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" disabled={createProperty.isPending}>
                  {createProperty.isPending ? "Creating…" : "Create property"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : properties && properties.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Address</th>
                  <th className="px-6 py-3 font-medium">City</th>
                  {showAgency && (
                    <th className="px-6 py-3 font-medium">Agency</th>
                  )}
                  <th className="px-6 py-3 font-medium tabular-nums">Rent</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border/60 last:border-0 hover:bg-muted/50"
                  >
                    <td className="px-6 py-3">
                      <Link
                        to={`/properties/${p.id}`}
                        className="font-medium text-foreground hover:text-primary"
                      >
                        {[p.street, p.house_number].filter(Boolean).join(" ") ||
                          p.reference ||
                          "Untitled property"}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">{p.city ?? "—"}</td>
                    {showAgency && (
                      <td className="px-6 py-3 text-muted-foreground">
                        {p.organization_name ?? "—"}
                      </td>
                    )}
                    <td className="px-6 py-3 tabular-nums">
                      {formatRent(p.advertised_rent)}
                    </td>
                    <td className="px-6 py-3">
                      <Badge tone={p.is_published ? "success" : "neutral"} dot>
                        {p.is_published ? "Published" : "Draft"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <Building2 className="size-8 opacity-40" />
              <p className="text-sm">
                {landlordView
                  ? "Your agency hasn't listed any properties for you yet."
                  : "No properties yet. Create your first one."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
